import React, { useEffect, useState } from "react";
import { Checkbox, Drawer, message, Spin } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import CustomButton from "../../commonComponents/button";
import CustomTypography, { TYPOGRAPHY } from "../../commonComponents/CustomTypography";
import { GET_SOURCE_EXPORT_PREFERENCE } from "../../../graphql/query";
import { SAVE_SOURCE_EXPORT_PREFERENCE } from "../../../graphql/mutation/mutation";

// Source-depth options — value maps 1:1 to the BE `sourceLevel` enum.
export const SOURCE_LEVEL = {
  PARENT: "PARENT",
  CHILD: "CHILD",
  ALL: "ALL",
};

const SOURCE_LEVEL_OPTIONS = [
  {
    value: SOURCE_LEVEL.PARENT,
    label: "Top-level sources only",
    description: "One row per parent source, with rolled-up counts.",
  },
  {
    value: SOURCE_LEVEL.CHILD,
    label: "Top-level + child sources",
    description: "Parent and child rows; sub-child counts stay rolled up.",
  },
  {
    value: SOURCE_LEVEL.ALL,
    label: "All levels",
    description: "Parent, child, and sub-child rows — the current export.",
  },
];

// A single selectable card in the source-depth list. Highlighted when picked,
// so the drawer's choices read like radio cards rather than plain radios.
// Colours follow the AntD Blue-6/Blue-1 palette the rest of the app uses for
// selected chips/cards (see LeadInfoCard / ToneChip).
const SourceLevelCard = ({ option, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(option.value)}
    className={`w-full text-left rounded-lg border border-solid px-4 py-3 cursor-pointer transition-colors bg-white hover:border-[#4096FF] ${
      selected ? "border-[#1677FF] bg-[#E6F4FF]" : "border-[#D9D9D9]"
    }`}
  >
    <div className="flex items-start gap-3">
      <span
        className={`mt-1 inline-flex items-center justify-center w-4 h-4 rounded-full border border-solid shrink-0 ${
          selected ? "border-[#1677FF]" : "border-[#D9D9D9]"
        }`}
      >
        {selected && <span className="w-2 h-2 rounded-full bg-[#1677FF]" />}
      </span>
      <div className="flex flex-col">
        <CustomTypography preset={TYPOGRAPHY.BODY_STRONG}>{option.label}</CustomTypography>
        <CustomTypography preset={TYPOGRAPHY.CAPTION} type="secondary">
          {option.description}
        </CustomTypography>
      </div>
    </div>
  </button>
);

/**
 * SourceExportPreferenceDrawer — configures how the "by Sources" export
 * is generated. The two preferences (source depth + hide-zero-rows) are
 * saved server-side per location; the export mutation reads them from
 * the saved preference, so the caller doesn't pass them explicitly.
 *
 * @param {Object} props
 * @param {boolean} props.open - Drawer visibility
 * @param {Function} props.onClose - Close handler
 * @param {number|string} props.locationId - Active location scope
 */
const SourceExportPreferenceDrawer = ({ open, onClose, locationId }) => {
  // Start unset so no card is pre-selected while the query is in flight —
  // the initial selection is written from the response (or the fallback
  // default) once loading finishes.
  const [sourceLevel, setSourceLevel] = useState(null);
  const [hideZeroRows, setHideZeroRows] = useState(false);

  // Load the saved preference for this location — pre-fills the drawer
  // so the user sees their last choice, not a reset default.
  const { data, loading } = useQuery(GET_SOURCE_EXPORT_PREFERENCE, {
    variables: { locationId: locationId != null ? Number(locationId) : undefined },
    skip: !open || locationId == null,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    // Wait for the query to actually resolve — an early !loading pass with
    // `data === undefined` (before the request fires) would otherwise seed
    // the fallback default and then swap when the response arrives.
    if (loading || !data) return;
    const saved = data.getSourceExportPreference;
    setSourceLevel(saved?.sourceLevel || SOURCE_LEVEL.ALL);
    setHideZeroRows(typeof saved?.hideZeroRows === "boolean" ? saved.hideZeroRows : false);
  }, [data, loading]);

  const [savePreference, { loading: saving }] = useMutation(SAVE_SOURCE_EXPORT_PREFERENCE);

  const handleSave = async () => {
    try {
      await savePreference({
        variables: {
          locationId: Number(locationId),
          sourceLevel,
          hideZeroRows,
        },
      });
      message.success("Export preferences saved");
      onClose?.();
    } catch (e) {
      message.error(`Failed to save preferences: ${e.message}`);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Export preferences"
      width={480}
      destroyOnClose
      styles={{ body: { padding: 24 } }}
      footer={
        <div className="flex items-center gap-2 py-2">
          <CustomButton
            type="primary"
            btnText="Save"
            handleClick={handleSave}
            loading={saving}
            disabled={loading || locationId === null}
          />
          <CustomButton btnText="Cancel" handleClick={onClose} disabled={saving} />
        </div>
      }
    >
      {/* Overlay the real UI with an AntD Spin while the saved preference
          loads — user sees the layout immediately, the spinner + caption
          on top signals the drawer is still hydrating from the server. */}
      <Spin spinning={loading} tip="Fetching your saved preferences…">
        <div className="flex flex-col mb-4">
          <CustomTypography preset={TYPOGRAPHY.SECTION_TITLE}>Source depth</CustomTypography>
          <CustomTypography preset={TYPOGRAPHY.CAPTION} type="secondary">
            Choose how many levels of the source hierarchy to include.
          </CustomTypography>
        </div>

        <div className="flex flex-col gap-3">
          {SOURCE_LEVEL_OPTIONS.map(option => (
            <SourceLevelCard
              key={option.value}
              option={option}
              selected={sourceLevel === option.value}
              onSelect={setSourceLevel}
            />
          ))}
        </div>

        <div className="mt-6 pt-4 border-0 border-t border-solid border-[#F0F0F0]">
          {/* Checkbox aligned to the label's first line; the caption below sits
              under the label (not under the checkbox) via the flex-column child. */}
          <Checkbox
            checked={hideZeroRows}
            onChange={e => setHideZeroRows(e.target.checked)}
            className="!items-start"
          >
            <div className="flex flex-col">
              <CustomTypography preset={TYPOGRAPHY.BODY_STRONG}>
                Exclude sources with all-zero data
              </CustomTypography>
              <CustomTypography preset={TYPOGRAPHY.CAPTION} type="secondary">
                Leave out rows where every metric is 0 for the selected period.
              </CustomTypography>
            </div>
          </Checkbox>
        </div>
      </Spin>
    </Drawer>
  );
};

export default SourceExportPreferenceDrawer;
